import React, { useCallback, useMemo, useRef, useState } from 'react';

import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@mui/material';
import CmtCard from '@coremat/CmtCard';
import CmtCardHeader from '@coremat/CmtCard/CmtCardHeader';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import {
  useActiveMerchandisingProgramQuote,
  useMerchandisingProgramQuote,
  usePatchApproveMerchandisingProgramQuote,
  usePatchMerchandisingProgramQuoteChangeInformation,
  usePostMerchandisingProgramQuoteCheckout,
} from 'services/Data/MerchandisingProgramQuoteService';
import GridContainer from '@jumbo/components/GridContainer';
import GridItem from '@jumbo/components/GridItem';
import MerchandisingProgramQuoteInfoCheckoutSection from './MerchandisingProgramQuoteInfoCheckoutSection';
import MerchandisingProgramQuoteDeliveryCheckoutSection from './MerchandisingProgramQuoteDeliveryCheckoutSection';
import { useHistory } from 'react-router-dom';
import BaseModal from 'Components/BaseModal';
import SimpleReactValidator from 'simple-react-validator';
import { validatorConfig } from 'services/Validators/Validation';
import useToast from 'services/Hooks/useToast';
import useForceUpdate from 'services/Hooks/useForceUpdate';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import CustomInput from '@acnb/CustomComponents/CustomInput/CustomInput';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import { MerchandisingProgramQuoteStatusEnumValue } from 'enums/MerchandisingProgramQuoteStatusEnum';
import MerchandisingProgramQuoteConsentments from './MerchandisingProgramQuoteConsentments';
import PhoneInput from '@acnb/CustomComponents/PhoneInput/PhoneInput';
import { homePage } from 'config/config';
import StorePageHeader from 'Components/StorePageHeader';

const useStyles = makeStyles(() => ({
  pageFull: {
    width: '100%',
  },
  noItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cartFooter: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  checkoutBtn: {
    marginTop: '1vh',
  },
  deliverySection: {
    position: 'sticky',
    top: '10vh',
  },
  card: {
    overflow: 'visible',
  },
}));

const MerchandisingProgramQuoteCheckout = ({ fromHistory }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const forceUpdate = useForceUpdate();

  const { errorMessage } = useToast();
  const validator = useRef(new SimpleReactValidator(validatorConfig.getConfig(t)));

  const [showCheckoutConfirmed, setShowCheckoutConfirmed] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [consentmentAccepted, setConsentmentAccepted] = useState(false);
  const [copyingKeyContactInformation, setCopyingKeyContactInformation] = useState(false);
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);

  const accountReducer = useSelector(state => state.AccountReducer);

  let { merchandisingProgramQuoteId } = useParams();

  // active cart is used in cart (it's the current cart being in use)
  // history cart is used in the history only
  const {
    data: activeMerchandisingProgramQuote,
    isLoading: gettingMerchandisingProgramQuote,
  } = useActiveMerchandisingProgramQuote(accountReducer.isLoggedIn);
  const {
    data: historyMerchandisingProgramQuote,
    isLoading: gettingMerchandisingProgramQuoteHistory,
  } = useMerchandisingProgramQuote(merchandisingProgramQuoteId);
  const {
    mutateAsync: changeInformations,
    isPending: changingInformations,
  } = usePatchMerchandisingProgramQuoteChangeInformation();

  const merchandisingProgramQuote = useMemo(
    () => (merchandisingProgramQuoteId ? historyMerchandisingProgramQuote : activeMerchandisingProgramQuote),
    [merchandisingProgramQuoteId, historyMerchandisingProgramQuote, activeMerchandisingProgramQuote],
  );

  const { mutate: merchandisingProgramQuoteCheckout, isPending: isCheckingOut } = usePostMerchandisingProgramQuoteCheckout();
  const { mutate: changeMerchandisingProgramQuoteStatut } = usePatchApproveMerchandisingProgramQuote();

  const handleCheckout = useCallback(() => {
    if (!validator.current.allValid()) {
      validator.current.showMessages();
      forceUpdate();

      errorMessage({
        message: 'contraint_errors',
      });
      return;
    }
    validator.current.hideMessages();

    merchandisingProgramQuoteCheckout(merchandisingProgramQuote);
    setShowCheckoutConfirmed(true);
  }, [errorMessage, merchandisingProgramQuote, merchandisingProgramQuoteCheckout, forceUpdate]);

  const handleCloseCancelMerchandisingProgramQuoteModal = useCallback(() => {
    setShowCancelModal(false);
  }, [setShowCancelModal]);

  const handleCloseConfirmedCheckoutModal = useCallback(() => {
    setShowCheckoutConfirmed(false);
  }, [setShowCheckoutConfirmed]);

  const handleChangeStatus = useCallback(
    newStatus => {
      changeMerchandisingProgramQuoteStatut({ ...merchandisingProgramQuote, status: newStatus });
    },
    [changeMerchandisingProgramQuoteStatut, merchandisingProgramQuote],
  );

  const isLoading = useMemo(
    () =>
      gettingMerchandisingProgramQuote || gettingMerchandisingProgramQuoteHistory || isCheckingOut || changingInformations,
    [changingInformations, gettingMerchandisingProgramQuote, gettingMerchandisingProgramQuoteHistory, isCheckingOut],
  );

  const actionButtons = useMemo(
    () => [
      {
        variant: 'contained',
        color: 'secondary',
        hidden: merchandisingProgramQuoteId,
        action:
          !merchandisingProgramQuoteId &&
          merchandisingProgramQuote?.status === MerchandisingProgramQuoteStatusEnumValue.Active &&
          handleCheckout,
        label: !merchandisingProgramQuoteId && t('proceedCheckoutMerchandisingProgramQuote'),
        disabled:
          merchandisingProgramQuote?.status !== MerchandisingProgramQuoteStatusEnumValue.Active ||
          !consentmentAccepted ||
          !merchandisingProgramQuote?.signature?.trim() ||
          !merchandisingProgramQuote?.signatureDate ||
          merchandisingProgramQuote?.isDemo,
      },
      {
        variant: 'contained',
        color: 'error',
        hidden: !merchandisingProgramQuoteId,
        action: () => setShowCancelModal(true),
        label: t('cancel'),
        disabled:
          merchandisingProgramQuote?.status !== MerchandisingProgramQuoteStatusEnumValue.Active &&
          merchandisingProgramQuote?.status !== MerchandisingProgramQuoteStatusEnumValue.InProcess,
      },
    ],
    [
      merchandisingProgramQuoteId,
      merchandisingProgramQuote?.status,
      merchandisingProgramQuote?.signature,
      merchandisingProgramQuote?.signatureDate,
      merchandisingProgramQuote?.isDemo,
      handleCheckout,
      t,
      consentmentAccepted,
    ],
  );

  const backUrl = useMemo(() => (fromHistory ? '/dealer/profile/request-histories' : '/dealer/profile/awaiting-requests'), [
    fromHistory,
  ]);

  return (
    <Box className={classes.pageFull}>
      <BaseModal
        show={showCheckoutConfirmed}
        title={t('merchandisingProgramQuoteCheckoutConfirmed')}
        message={t('merchandisingProgramQuoteCheckoutConfirmedText')}
        closeBtnMessage={t('close')}
        onClose={handleCloseConfirmedCheckoutModal}
        actions={[
          {
            onClick: () => history.push(homePage),
            color: 'secondary',
            style: { color: 'black', borderRadius: 0 },
            variant: 'contained',
            message: t('goToHome'),
          },
        ]}
      />
      <BaseModal
        show={showCancelModal}
        title={t('cancelMerchandisingProgramQuote')}
        message={t('cancelMerchandisingProgramQuoteText')}
        closeBtnMessage={t('close')}
        onClose={handleCloseCancelMerchandisingProgramQuoteModal}
        actions={[
          {
            onClick: () => {
              handleChangeStatus(MerchandisingProgramQuoteStatusEnumValue.Cancelled);
              history.push(backUrl);
            },
            color: 'error',
            style: { color: 'white' },
            variant: 'contained',
            message: t('cancel'),
          },
        ]}
      />
      <Box sx={{ mb: 9 }}>
        <StorePageHeader title={t('quote')} imgLeft backgroundColor="lightGray" borderRadius="0.5rem" />
      </Box>
      <CmtCard className={classes.card}>
        <CmtCardHeader>
          {merchandisingProgramQuoteId && (
            <Button
              onClick={() => history.push(backUrl)}
              variant="outlined"
              sx={{
                borderRadius: 0,
              }}>
              {t('backToList')}
            </Button>
          )}
        </CmtCardHeader>
        <CmtCardContent>
          <GridContainer>
            <GridItem xs={12} md={6}>
              <Box className={classes.deliverySection}>
                <Typography variant="h1">{t('contactInformation')}</Typography>
                <Box sx={{ my: 5 }}>
                  <GridContainer>
                    <GridItem xs={12}>
                      <SkeletonWrapper
                        skeletonProps={{
                          width: 210,
                          height: 33,
                        }}
                        isLoading={gettingMerchandisingProgramQuote}
                        component={
                          merchandisingProgramQuote && (
                            <CustomInput
                              labelText={t('contactName')}
                              formControlProps={{
                                fullWidth: true,
                              }}
                              error={validator.current.message(
                                t('contactName'),
                                merchandisingProgramQuote?.contactName,
                                `required`,
                              )}
                              inputProps={{
                                onBlur: ({ target: { value } }) => {
                                  changeInformations({ ...merchandisingProgramQuote, contactName: value });
                                },
                                disabled: merchandisingProgramQuoteId,
                                defaultValue: merchandisingProgramQuote.contactName || '',
                              }}
                            />
                          )
                        }
                      />
                    </GridItem>
                    <GridItem xs={12}>
                      <SkeletonWrapper
                        skeletonProps={{
                          width: 210,
                          height: 33,
                        }}
                        isLoading={gettingMerchandisingProgramQuote}
                        component={
                          merchandisingProgramQuote && (
                            <CustomInput
                              labelText={t('email')}
                              id="email"
                              formControlProps={{
                                fullWidth: true,
                              }}
                              error={validator.current.message(
                                t('email'),
                                merchandisingProgramQuote?.contactEmail,
                                `required|email`,
                              )}
                              inputProps={{
                                onBlur: ({ target: { value } }) => {
                                  changeInformations({ ...merchandisingProgramQuote, contactEmail: value });
                                },
                                disabled: merchandisingProgramQuoteId,
                                defaultValue: merchandisingProgramQuote.contactEmail || '',
                              }}
                            />
                          )
                        }
                      />
                    </GridItem>
                    <GridItem xs={12}>
                      <SkeletonWrapper
                        skeletonProps={{
                          width: 210,
                          height: 33,
                        }}
                        isLoading={gettingMerchandisingProgramQuote}
                        component={
                          merchandisingProgramQuote && (
                            <CustomInput
                              labelText={t('phone')}
                              id="phone"
                              labelProps={{
                                shrink: isPhoneFocused || merchandisingProgramQuote?.contactPhoneNumber,
                              }}
                              formControlProps={{
                                fullWidth: true,
                              }}
                              error={validator.current.message(
                                t('phone'),
                                merchandisingProgramQuote?.contactPhoneNumber,
                                `required`,
                              )}
                              inputProps={{
                                inputComponent: PhoneInput,
                                defaultValue: merchandisingProgramQuote?.contactPhoneNumber,
                                onFocus: () => setIsPhoneFocused(true),
                                onBlur: ({ target: { value } }) => {
                                  setIsPhoneFocused(false);

                                  if (value !== merchandisingProgramQuote?.contactPhoneNumber) {
                                    changeInformations({ ...merchandisingProgramQuote, contactPhoneNumber: value });
                                  }
                                },
                                disabled: merchandisingProgramQuoteId,
                              }}
                            />
                          )
                        }
                      />
                    </GridItem>
                  </GridContainer>
                </Box>

                <Typography variant="h1">{t('keyContactInformation')}</Typography>
                <Typography fontSize={'smaller'}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('keyContactInformationInfo_1'),
                    }}
                  />
                </Typography>
                <Typography sx={{ mt: 2 }} fontSize={'smaller'}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('keyContactInformationInfo_2'),
                    }}
                  />
                </Typography>

                <Box sx={{ my: 5 }}>
                  <GridContainer>
                    <GridItem xs={12}>
                      <Button
                        sx={{ color: '#000' }}
                        color="secondary"
                        variant="contained"
                        disabled={merchandisingProgramQuoteId}
                        onClick={() => {
                          setCopyingKeyContactInformation(true);
                          changeInformations({
                            ...merchandisingProgramQuote,
                            keyContactName: merchandisingProgramQuote.contactName,
                            keyContactPhoneNumber: merchandisingProgramQuote.contactPhoneNumber,
                            keyContactEmail: merchandisingProgramQuote.contactEmail,
                          }).then(_ => setCopyingKeyContactInformation(false));
                        }}>
                        {t('copyContactInformation')}
                      </Button>
                    </GridItem>
                    <GridItem xs={12}>
                      <SkeletonWrapper
                        skeletonProps={{
                          width: 210,
                          height: 33,
                        }}
                        isLoading={gettingMerchandisingProgramQuote || copyingKeyContactInformation}
                        component={
                          merchandisingProgramQuote && (
                            <CustomInput
                              labelText={t('keyContactName')}
                              formControlProps={{
                                fullWidth: true,
                              }}
                              inputProps={{
                                onBlur: ({ target: { value } }) => {
                                  changeInformations({ ...merchandisingProgramQuote, keyContactName: value });
                                },
                                disabled: merchandisingProgramQuoteId,
                                defaultValue: merchandisingProgramQuote.keyContactName || '',
                              }}
                            />
                          )
                        }
                      />
                    </GridItem>
                    <GridItem xs={12}>
                      <SkeletonWrapper
                        skeletonProps={{
                          width: 210,
                          height: 33,
                        }}
                        isLoading={gettingMerchandisingProgramQuote || copyingKeyContactInformation}
                        component={
                          merchandisingProgramQuote && (
                            <CustomInput
                              labelText={t('keyEmail')}
                              formControlProps={{
                                fullWidth: true,
                              }}
                              error={validator.current.message(
                                t('keyEmail'),
                                merchandisingProgramQuote?.keyContactEmail,
                                `email`,
                              )}
                              inputProps={{
                                onBlur: ({ target: { value } }) => {
                                  changeInformations({ ...merchandisingProgramQuote, keyContactEmail: value });
                                },
                                disabled: merchandisingProgramQuoteId,
                                defaultValue: merchandisingProgramQuote.keyContactEmail || '',
                              }}
                            />
                          )
                        }
                      />
                    </GridItem>
                    <GridItem xs={12}>
                      <SkeletonWrapper
                        skeletonProps={{
                          width: 210,
                          height: 33,
                        }}
                        isLoading={gettingMerchandisingProgramQuote || copyingKeyContactInformation}
                        component={
                          merchandisingProgramQuote && (
                            <CustomInput
                              labelText={t('keyPhone')}
                              id="phone"
                              labelProps={{
                                shrink: isPhoneFocused || merchandisingProgramQuote?.keyContactPhoneNumber,
                              }}
                              formControlProps={{
                                fullWidth: true,
                              }}
                              inputProps={{
                                inputComponent: PhoneInput,
                                defaultValue: merchandisingProgramQuote?.keyContactPhoneNumber,
                                onFocus: () => setIsPhoneFocused(true),
                                onBlur: ({ target: { value } }) => {
                                  setIsPhoneFocused(false);

                                  if (value !== merchandisingProgramQuote?.keyContactPhoneNumber) {
                                    changeInformations({ ...merchandisingProgramQuote, keyContactPhoneNumber: value });
                                  }
                                },
                                disabled: merchandisingProgramQuoteId,
                              }}
                            />
                          )
                        }
                      />
                    </GridItem>
                  </GridContainer>
                </Box>

                <Typography variant="h1">{t('commentOrQuestions')}</Typography>
                <SkeletonWrapper
                  skeletonProps={{
                    width: 210,
                    height: 33,
                  }}
                  isLoading={gettingMerchandisingProgramQuote}
                  component={
                    merchandisingProgramQuote && (
                      <CustomInput
                        textfieldProps={{ multiline: true, rows: 2 }}
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          onBlur: ({ target: { value } }) => {
                            changeInformations({ ...merchandisingProgramQuote, commentOrQuestions: value });
                          },
                          disabled: merchandisingProgramQuoteId,
                          defaultValue: merchandisingProgramQuote.commentOrQuestions || '',
                        }}
                      />
                    )
                  }
                />
                <Typography sx={{ mt: 3 }} variant="h1">
                  {t('dealerAddress')}
                </Typography>
                <MerchandisingProgramQuoteDeliveryCheckoutSection
                  merchandisingProgramQuote={merchandisingProgramQuote}
                  isLoading={isLoading}
                  validator={validator}
                  isDisabled={merchandisingProgramQuote?.status !== MerchandisingProgramQuoteStatusEnumValue.Active}
                  actionBtns={actionButtons}
                />
              </Box>
            </GridItem>
            <GridItem xs={12} md={6}>
              <Box>
                <Typography variant="h1">{t('verifyQuote')}</Typography>
                <Typography variant="h5">{t('verifyQuoteInfo')}</Typography>
                <Typography fontWeight={'bold'} variant="h5">
                  {t('verifyQuoteWarningBold')}
                </Typography>

                <Typography fontWeight={'bold'} variant="h5" sx={{ mt: 3 }}>
                  {t('verifyQuoteAdditionalDetails')}
                </Typography>
                <Box sx={{ ml: 5 }}>
                  <ul style={{ listStyleType: 'decimal' }}>
                    <li>{t('verifyQuoteAdditionalDetail_1')}</li>
                    <li>{t('verifyQuoteAdditionalDetail_2')}</li>
                    <li>{t('verifyQuoteAdditionalDetail_3')}</li>
                    {t('verifyQuoteAdditionalDetail_4') && <li>{t('verifyQuoteAdditionalDetail_4')}</li>}
                  </ul>
                </Box>

                <Typography variant="h1" sx={{ mt: 3 }}>
                  {t('quoteInfo')}
                </Typography>
                <MerchandisingProgramQuoteInfoCheckoutSection
                  validator={validator}
                  merchandisingProgramQuote={merchandisingProgramQuote}
                  isLoading={isLoading}
                  isDisabled={merchandisingProgramQuoteId}
                />
              </Box>
            </GridItem>

            <GridItem xs={12}>
              <MerchandisingProgramQuoteConsentments
                setConsentmentAccepted={setConsentmentAccepted}
                consentmentAccepted={consentmentAccepted}
                merchandisingProgramQuote={merchandisingProgramQuote}
                validator={validator}
                isDisabled={merchandisingProgramQuoteId}
              />
            </GridItem>
            <GridItem xs={12}>
              {actionButtons
                .filter(b => !b.hidden)
                .map(btnInfo => (
                  <Button
                    className={classes.checkoutBtn}
                    sx={{ borderRadius: 0, color: '#000' }}
                    variant={btnInfo.variant}
                    color={btnInfo.color}
                    disabled={btnInfo.disabled}
                    onClick={() => btnInfo.action()}>
                    {btnInfo.label}
                  </Button>
                ))}
            </GridItem>
          </GridContainer>
        </CmtCardContent>
      </CmtCard>
    </Box>
  );
};

export default MerchandisingProgramQuoteCheckout;
